/**
 * AdminProjectDetailPage — [UC-022, UC-023, UC-056, UC-063]
 * Hero + progress stepper + use cases + admin actions. Mirrors the user
 * project detail layout so admins see the same project state progression.
 */
import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { CheckCircle, XCircle } from 'lucide-react';
import toast from 'react-hot-toast';
import { api } from '../core/api-client';
import { fmt, fmtMoney } from '../utils/format';
import { ExpandableMarkdown } from '../components/ui/ExpandableMarkdown';
import { ProjectLifecycleStepper } from '../components/ui/ProjectLifecycleStepper';

interface UseCase {
  projectUseCaseId: number;
  name: string;
  description?: string;
  price: number | string;
  mandatory: boolean;
  implementationStatus?: string;
  sprint?: number | null;
  sprintStartedAt?: string | null;
}

interface ChangeRequest {
  changeRequestId: number;
  projectId: number;
  screenId: number | null;
  title: string;
  description: string;
  priority: string;
  status: string;
  creationDate: string;
  resolutionMethod?: string;
  newPreviewLink?: string;
}

interface Screen {
  screenId: number;
  name: string;
  projectId: number;
  filePath?: string;
}

interface Incident {
  incidentId: number;
  projectId: number;
  description: string;
  status: string;
  creationDate: string;
  categorizationType?: string;
  reasoning?: string;
  closedAt?: string;
}

interface TimelineOption {
  timelineOptionId: number;
  name?: string;
  description?: string;
  percentageIncrease?: number | string;
}

interface ProjectDetail {
  projectId: number;
  name: string;
  description: string;
  status: string;
  creationDate: string;
  userId: number;
  previewLink?: string;
  appLink?: string;
  developmentPrice?: number | string;
  totalPrice?: number | string;
  zipFilePath?: string;
  useCases: UseCase[];
  changeRequests?: ChangeRequest[];
  screens?: Screen[];
  incidents?: Incident[];
  currentSprint?: number;
  timelineOptionId?: number;
  timelineOptions?: TimelineOption[];
}

function statusBadgeClass(status: string): string {
  switch (status) {
    case 'App preview preparation': return 'bg-blue-600/15 text-blue-700 dark:text-blue-500 border-blue-600/25';
    case 'Rejected': return 'bg-red-600/15 text-red-700 dark:text-red-500 border-red-600/25';
    case 'App preview feedback': return 'bg-amber-600/15 text-amber-700 dark:text-amber-500 border-amber-600/25';
    case 'Technical analysis': return 'bg-cyan-600/15 text-cyan-700 dark:text-cyan-500 border-cyan-600/25';
    case 'Sprint planning': return 'bg-violet-600/15 text-violet-700 dark:text-violet-500 border-violet-600/25';
    case 'For testing': return 'bg-[var(--accent-primary)]/15 text-[var(--accent-primary)] border-[var(--accent-primary)]/25';
    case 'In development': return 'bg-purple-600/15 text-purple-700 dark:text-purple-500 border-purple-600/25';
    case 'Finished testing':
    case 'Delivered': return 'bg-green-600/15 text-green-700 dark:text-green-500 border-green-600/25';
    default: return 'bg-[var(--surface-overlay)] text-[var(--text-secondary)] border-[var(--border-subtle)]';
  }
}

function normalizeStatus(raw: string | undefined): string {
  if (!raw) return '';
  if (raw.includes(' ')) return raw;
  const parts = raw.replace(/([a-z])([A-Z])/g, '$1 $2').split(' ');
  if (parts.length === 0) return raw;
  return [parts[0], ...parts.slice(1).map((w) => w.toLowerCase())].join(' ');
}

function num(v: number | string | undefined | null): number {
  if (v === undefined || v === null) return 0;
  return typeof v === 'number' ? v : Number(v);
}

export function AdminProjectDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [project, setProject] = useState<ProjectDetail | null>(null);
  const [changeRequests, setChangeRequests] = useState<ChangeRequest[]>([]);
  const [screens, setScreens] = useState<Screen[]>([]);
  const [incidents, setIncidents] = useState<Incident[]>([]);
  const [loading, setLoading] = useState(true);
  // Inline CR status workflow — selection set + in-flight guard.
  const [selectedCRIds, setSelectedCRIds] = useState<Set<number>>(new Set());
  const [bulkBusy, setBulkBusy] = useState(false);

  const loadProject = async () => {
    if (!id) return;
    try {
      setLoading(true);
      const response = await api.get<ProjectDetail>(`/projects/${id}`);
      response.status = normalizeStatus(response.status);
      setProject(response);
      setIncidents(response.incidents ?? []);
    } catch (error) {
      console.error('Failed to load project:', error);
      toast.error(t('admin.projects.detail.loadError'));
    } finally {
      setLoading(false);
    }
  };

  const loadChangeRequests = async () => {
    if (!id) return;
    try {
      // Fetch ALL CRs — the component partitions them into
      // New (pending / actionable) and Resolved (Processed / Captured /
      // Finished) sections client-side.
      const response = await api.get<{ items: ChangeRequest[] }>(
        `/projects/${id}/change-requests`,
      );
      setChangeRequests(response.items || []);
    } catch (error) {
      console.error('Failed to load change requests:', error);
    }
  };

  const loadScreens = async () => {
    if (!id) return;
    try {
      // Response is PaginatedResponse[ScreenResponse].
      const response = await api.get<{ items: Screen[] }>(`/projects/${id}/screens`);
      setScreens(response.items || []);
    } catch (error) {
      console.error('Failed to load screens:', error);
    }
  };

  // Captured is the default / pending state (what users submit). The only
  // admin transition is Captured -> Processed. Legacy rows saved as "New"
  // before the default change are treated as pending on the client and
  // accepted by the backend.
  const isPendingCR = (cr: ChangeRequest) => cr.status === 'Captured' || cr.status === 'New';

  const applyProcessed = async (crId: number): Promise<boolean> => {
    try {
      await api.patch(`/change-requests/${crId}/status`, { status: 'Processed' });
      return true;
    } catch (error) {
      console.error(`Failed to mark CR ${crId} Processed:`, error);
      return false;
    }
  };

  const toggleCRSelected = (crId: number) => {
    setSelectedCRIds((prev) => {
      const next = new Set(prev);
      if (next.has(crId)) next.delete(crId);
      else next.add(crId);
      return next;
    });
  };

  const toggleSelectAllPending = () => {
    const pendingIds = changeRequests.filter(isPendingCR).map((cr) => cr.changeRequestId);
    const allSelected = pendingIds.length > 0 && pendingIds.every((id) => selectedCRIds.has(id));
    setSelectedCRIds(allSelected ? new Set() : new Set(pendingIds));
  };

  const handleRowProcessed = async (crId: number) => {
    const ok = await applyProcessed(crId);
    if (ok) {
      toast.success(t('adminProjectDetail.status.processedApplied', 'Marked as Processed'));
      setSelectedCRIds((prev) => {
        const next = new Set(prev);
        next.delete(crId);
        return next;
      });
      await loadChangeRequests();
    } else {
      toast.error(t('adminProjectDetail.status.error', 'Failed to update status'));
    }
  };

  const handleBulkProcessed = async () => {
    if (selectedCRIds.size === 0 || bulkBusy) return;
    setBulkBusy(true);
    try {
      const ids = Array.from(selectedCRIds);
      const results = await Promise.all(ids.map((id) => applyProcessed(id)));
      const ok = results.filter(Boolean).length;
      const fail = ids.length - ok;
      if (ok > 0) {
        toast.success(
          t('adminProjectDetail.status.processedBulkApplied', '{{count}} marked as Processed')
            .replace('{{count}}', String(ok)),
        );
      }
      if (fail > 0) {
        toast.error(
          t('adminProjectDetail.status.bulkError', '{{count}} failed').replace('{{count}}', String(fail)),
        );
      }
      setSelectedCRIds(new Set());
      await loadChangeRequests();
    } finally {
      setBulkBusy(false);
    }
  };

  useEffect(() => {
    loadProject();
    loadChangeRequests();
    loadScreens();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const handleApprove = () => navigate(`/admin/projects/${id}/review`);
  const handleReject = () => navigate(`/admin/projects/${id}/reject`);
  const handleManageUseCases = () => navigate(`/admin/projects/${id}/add-use-cases`);
  const handleManageScreens = () => navigate(`/admin/projects/${id}/screens`);
  // [UC-026]
  const handleCompleteDevelopment = () => navigate(`/admin/development-complete/${id}`);

  const [finishingTA, setFinishingTA] = useState(false);
  const handleFinishTechnicalAnalysis = async () => {
    if (!id || finishingTA) return;
    const ok = window.confirm(
      t(
        'adminProjectDetail.finishTA.confirm',
        'Finish technical analysis for this project? The user will be emailed and can then pick use cases and a timeline to start development.',
      ),
    );
    if (!ok) return;
    try {
      setFinishingTA(true);
      await api.post(`/projects/${id}/finish-technical-analysis`);
      toast.success(
        t('adminProjectDetail.finishTA.success', 'Technical analysis finished — sprint planning unlocked'),
      );
      await loadProject();
    } catch (error) {
      const err = error as { response?: { data?: { detail?: string } } };
      toast.error(
        err?.response?.data?.detail ||
          t('adminProjectDetail.finishTA.error', 'Failed to finish technical analysis'),
      );
    } finally {
      setFinishingTA(false);
    }
  };

  const [finishingTesting, setFinishingTesting] = useState(false);
  const handleFinishTesting = async () => {
    if (!id || finishingTesting) return;
    const ok = window.confirm(
      t(
        'adminProjectDetail.finishTesting.confirm',
        'Mark testing as finished for this project? The user will see the project move out of the testing phase.',
      ),
    );
    if (!ok) return;
    try {
      setFinishingTesting(true);
      await api.patch(`/projects/${id}/status`, { status: 'FinishedTesting' });
      toast.success(
        t('adminProjectDetail.finishTesting.success', 'Testing marked as finished'),
      );
      await loadProject();
    } catch (error) {
      const err = error as { response?: { data?: { detail?: string } } };
      toast.error(
        err?.response?.data?.detail ||
          t('adminProjectDetail.finishTesting.error', 'Failed to update project status'),
      );
    } finally {
      setFinishingTesting(false);
    }
  };

  if (loading) {
    return (
      <div className="p-4 sm:p-6 lg:p-8">
        <div className="flex items-center justify-center py-20">
          <div className="w-8 h-8 border-4 border-[var(--accent-primary)] border-t-transparent rounded-full animate-spin"></div>
        </div>
      </div>
    );
  }

  if (!project) {
    return (
      <div className="p-4 sm:p-6 lg:p-8">
        <div className="bg-red-600/15 border border-red-600/25 rounded-xl p-4">
          <p className="text-red-700 text-sm">{t('admin.projects.detail.notFound')}</p>
          <button onClick={() => navigate('/admin/project-list')} className="mt-3 px-4 py-2 bg-red-600 text-white rounded-lg text-sm hover:bg-red-700">
            Back to projects
          </button>
        </div>
      </div>
    );
  }

  const status = project.status;
  const mandatoryUseCases = project.useCases?.filter((u) => u.mandatory) ?? [];
  const optionalUseCases = project.useCases?.filter((u) => !u.mandatory) ?? [];
  const pendingReview = status === 'App preview preparation';
  const technicalAnalysis = status === 'Technical analysis';
  const inDevelopment = status === 'In development';
  const forTesting = status === 'For testing';
  // normalizeStatus turns "FinishedTesting" into "Finished testing" before
  // the component sees it, so we compare against the normalized form.
  const finishedTesting = status === 'Finished testing';

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-full overflow-x-hidden">
      {/* Lifecycle stepper — identical to the user-side view so admin + user
          see the same progress representation. */}
      <ProjectLifecycleStepper status={status} className="mb-6" />

      {/* Hero Header Card */}
      <div className="mb-6 bg-[var(--surface-base)] rounded-xl border border-[var(--border-subtle)] shadow-sm overflow-hidden">
        <div className="h-1.5 bg-gradient-to-r from-[var(--accent-primary)] to-blue-400"></div>
        <div className="p-5 sm:p-6">
          <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
            <div className="flex-1 min-w-0">
              <h1 className="text-2xl lg:text-3xl font-bold text-[var(--text-primary)] break-words">{project.name}</h1>
              <ExpandableMarkdown
                text={project.description || ''}
                className="mt-2 text-base sm:text-sm text-[var(--text-secondary)]"
              />
              <div className="mt-3 flex flex-wrap items-center gap-2">
                <span className={`px-2.5 py-1 text-xs font-medium rounded-full border ${statusBadgeClass(status)}`}>
                  {status}
                </span>
                <span className="px-2.5 py-1 text-xs font-medium rounded-full bg-[var(--surface-raised)] text-[var(--text-secondary)] border border-[var(--border-subtle)]">
                  User #{project.userId}
                </span>
                <span className="px-2.5 py-1 text-xs font-medium rounded-full bg-[var(--surface-raised)] text-[var(--text-secondary)] border border-[var(--border-subtle)]">
                  Created {new Date(project.creationDate).toLocaleDateString()}
                </span>
              </div>
            </div>
            {project.previewLink && (
              <a href={project.previewLink} target="_blank" rel="noopener noreferrer"
                className="inline-flex items-center flex-shrink-0 px-5 py-2.5 bg-[var(--accent-primary)] text-[var(--accent-text)] rounded-lg hover:bg-[var(--accent-secondary)] text-sm font-medium transition-colors">
                View App Preview
                <svg className="ml-2 h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                </svg>
              </a>
            )}
          </div>
        </div>
      </div>

      {/* Sprint progress — which use cases are in which sprint and what
          state they're in. Admin needs this during development/testing to
          know exactly what the current delivery covers. Mirrors the card
          on the user-facing project detail so both sides see the same
          representation. */}
      {status !== 'App preview preparation' && (() => {
        const useCases = project.useCases || [];
        const stamped = useCases.filter(u => typeof u.sprint === 'number' && u.sprint !== null);
        if (stamped.length === 0) return null;
        const currentSprint = project.currentSprint ?? 1;
        const selectedTimeline = project.timelineOptions?.find(
          (opt) => opt.timelineOptionId === project.timelineOptionId
        );
        const timelinePct = selectedTimeline ? Math.round(num(selectedTimeline.percentageIncrease)) : null;
        const bySprint = new Map<number, UseCase[]>();
        for (const uc of stamped) {
          const s = uc.sprint as number;
          if (!bySprint.has(s)) bySprint.set(s, []);
          bySprint.get(s)!.push(uc);
        }
        const sortedSprints = Array.from(bySprint.keys()).sort((a, b) => a - b);

        const ucStatus = (uc: UseCase): { label: string; tone: 'done' | 'active' | 'pending' } => {
          if (uc.implementationStatus === 'Implemented') {
            return { label: 'Delivered', tone: 'done' };
          }
          if (uc.sprint === currentSprint) {
            if (status === 'In development') return { label: 'In development', tone: 'active' };
            if (status === 'For testing') return { label: 'In testing', tone: 'active' };
            if (status === 'Finished testing') return { label: 'Testing complete', tone: 'active' };
            return { label: 'Scheduled', tone: 'pending' };
          }
          return { label: 'Pending', tone: 'pending' };
        };
        const toneClass = (tone: 'done' | 'active' | 'pending') =>
          tone === 'done'
            ? 'bg-green-600/15 text-green-700 dark:text-green-500 border-green-600/25'
            : tone === 'active'
              ? 'bg-[var(--accent-primary)]/15 text-[var(--accent-primary)] border-[var(--accent-primary)]/25'
              : 'bg-[var(--surface-overlay)] text-[var(--text-secondary)] border-[var(--border-subtle)]';

        return (
          <div className="mb-6 bg-[var(--surface-base)] rounded-xl border border-[var(--border-subtle)] shadow-sm p-4 sm:p-5">
            <div className="flex items-center justify-between gap-3 mb-4">
              <h2 className="flex items-center gap-2.5 text-lg sm:text-xl font-semibold text-[var(--text-primary)]">
                <span className="flex items-center justify-center w-7 h-7 rounded-lg bg-[var(--accent-primary)]/10">
                  <svg className="h-4 w-4 text-[var(--accent-primary)]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M13 10V3L4 14h7v7l9-11h-7z" />
                  </svg>
                </span>
                Sprint progress
              </h2>
              <div className="flex items-center gap-2 flex-wrap justify-end">
                {selectedTimeline && (
                  <span
                    className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold bg-amber-600/15 text-amber-700 dark:text-amber-500 border border-amber-600/25"
                    title={selectedTimeline.description || selectedTimeline.name || ''}
                  >
                    <svg className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2.5">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    {selectedTimeline.name || 'Timeline'}
                    {timelinePct !== null && timelinePct !== 0 && (
                      <span className="font-bold">+{timelinePct}%</span>
                    )}
                  </span>
                )}
                <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold bg-[var(--accent-primary)]/15 text-[var(--accent-primary)] border border-[var(--accent-primary)]/25">
                  Current: Sprint {currentSprint}
                </span>
              </div>
            </div>
            <div className="space-y-5">
              {sortedSprints.map(s => {
                const isCurrent = s === currentSprint;
                // Every UC stamped in a single initiate_development call
                // shares the same sprintStartedAt — pick the first non-null
                // value as the kickoff timestamp for this sprint.
                const startedAtRaw = bySprint.get(s)!
                  .map(uc => uc.sprintStartedAt)
                  .find(v => !!v);
                let startedAtLabel = '';
                if (startedAtRaw) {
                  try {
                    startedAtLabel = new Date(startedAtRaw).toLocaleString();
                  } catch {
                    startedAtLabel = String(startedAtRaw).slice(0, 16).replace('T', ' ');
                  }
                }
                return (
                  <div key={s}>
                    <div className="flex items-center gap-2 mb-2 flex-wrap">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold border ${
                        isCurrent
                          ? 'bg-[var(--accent-primary)]/15 text-[var(--accent-primary)] border-[var(--accent-primary)]/25'
                          : 'bg-[var(--surface-raised)] text-[var(--text-secondary)] border-[var(--border-subtle)]'
                      }`}>
                        Sprint {s}
                      </span>
                      <span className="text-xs text-[var(--text-muted)]">
                        {bySprint.get(s)!.length} use case(s)
                      </span>
                      {startedAtLabel && (
                        <span
                          className="inline-flex items-center gap-1 text-xs text-[var(--text-muted)]"
                          title="When the user clicked Initiate Development for this sprint"
                        >
                          <svg className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                          </svg>
                          Initiated {startedAtLabel}
                        </span>
                      )}
                    </div>
                    <ul className="space-y-1.5 pl-1">
                      {bySprint.get(s)!.map(uc => {
                        const ucs = ucStatus(uc);
                        return (
                          <li key={uc.projectUseCaseId} className="flex items-center gap-2 text-sm text-[var(--text-primary)]">
                            {ucs.tone === 'done' ? (
                              <svg className="h-4 w-4 text-green-600 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="3">
                                <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                              </svg>
                            ) : ucs.tone === 'active' ? (
                              <svg className="h-4 w-4 text-[var(--accent-primary)] flex-shrink-0 animate-pulse" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                                <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                              </svg>
                            ) : (
                              <div className="h-4 w-4 rounded-full border-2 border-[var(--border-default)] flex-shrink-0" />
                            )}
                            <span className="flex-1">{uc.name}</span>
                            {uc.mandatory && (
                              <span className="text-[10px] uppercase font-semibold text-[var(--text-muted)]">Mandatory</span>
                            )}
                            <span className={`px-2 py-0.5 text-[10px] font-medium rounded-full border ${toneClass(ucs.tone)}`}>
                              {ucs.label}
                            </span>
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                );
              })}
            </div>
          </div>
        );
      })()}

      {/* Admin Actions */}
      <div className="mb-6 bg-[var(--surface-base)] rounded-xl border border-[var(--border-subtle)] shadow-sm p-4 sm:p-5 flex flex-wrap gap-3 justify-end">
        {pendingReview && (
          <>
            <button onClick={handleApprove}
              className="inline-flex items-center gap-2 px-5 py-2.5 bg-green-600 text-white rounded-lg hover:bg-green-700 text-sm font-medium transition-colors">
              <CheckCircle className="h-4 w-4" />
              Approve
            </button>
            <button onClick={handleReject}
              className="inline-flex items-center gap-2 px-5 py-2.5 bg-red-600 text-white rounded-lg hover:bg-red-700 text-sm font-medium transition-colors">
              <XCircle className="h-4 w-4" />
              Reject
            </button>
          </>
        )}
        {technicalAnalysis && (
          <>
            <button
              type="button"
              onClick={handleManageUseCases}
              className="inline-flex items-center gap-2 px-5 py-2.5 bg-[var(--surface-raised)] text-[var(--text-primary)] rounded-lg hover:bg-[var(--surface-overlay)] border border-[var(--border-subtle)] text-sm font-medium transition-colors"
            >
              {t('adminProjectDetail.scopeUseCases', 'Scope use cases & prices')}
            </button>
            <button
              type="button"
              onClick={handleFinishTechnicalAnalysis}
              disabled={finishingTA}
              className="inline-flex items-center gap-2 px-5 py-2.5 bg-green-600 text-white rounded-lg hover:bg-green-700 text-sm font-medium transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
            >
              <CheckCircle className="h-4 w-4" />
              {finishingTA
                ? t('adminProjectDetail.finishTA.busy', 'Finishing…')
                : t('adminProjectDetail.finishTA.action', 'Finish technical analysis')}
            </button>
          </>
        )}
        {inDevelopment && (
          <button onClick={handleCompleteDevelopment}
            className="inline-flex items-center gap-2 px-5 py-2.5 bg-green-600 text-white rounded-lg hover:bg-green-700 text-sm font-medium transition-colors">
            <CheckCircle className="h-4 w-4" />
            Complete Development
          </button>
        )}
        {forTesting && (
          <button
            onClick={handleFinishTesting}
            disabled={finishingTesting}
            className="inline-flex items-center gap-2 px-5 py-2.5 bg-green-600 text-white rounded-lg hover:bg-green-700 text-sm font-medium transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
          >
            <CheckCircle className="h-4 w-4" />
            {finishingTesting
              ? t('adminProjectDetail.finishTesting.busy', 'Finishing…')
              : t('adminProjectDetail.finishTesting.action', 'Mark testing as finished')}
          </button>
        )}
        {finishedTesting && (
          <button
            onClick={() => navigate(`/admin/code-delivery/${project.projectId}`)}
            className="inline-flex items-center gap-2 px-5 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-medium transition-colors"
          >
            <CheckCircle className="h-4 w-4" />
            {t('adminProjectDetail.deliverCode', 'Deliver Code')}
          </button>
        )}
        <button onClick={handleManageScreens}
          className="px-5 py-2.5 bg-[var(--surface-raised)] text-[var(--text-primary)] rounded-lg hover:bg-[var(--surface-overlay)] border border-[var(--border-subtle)] text-sm font-medium transition-colors">
          Manage Screens
        </button>
        <button onClick={handleManageUseCases}
          className="px-5 py-2.5 bg-[var(--surface-raised)] text-[var(--text-primary)] rounded-lg hover:bg-[var(--surface-overlay)] border border-[var(--border-subtle)] text-sm font-medium transition-colors">
          Manage Use Cases
        </button>
      </div>

      {/* Preview / Delivery Info */}
      {(project.previewLink || project.appLink || project.zipFilePath) && (
        <div className="mb-6 bg-[var(--surface-base)] rounded-xl border border-[var(--border-subtle)] shadow-sm p-4 sm:p-5 space-y-3">
          <h2 className="text-lg font-semibold text-[var(--text-primary)]">Project Artifacts</h2>
          {project.previewLink && (
            <div className="flex items-start justify-between gap-3">
              <span className="text-sm text-[var(--text-secondary)]">Preview Link</span>
              <a href={project.previewLink} target="_blank" rel="noopener noreferrer"
                className="text-sm font-medium text-[var(--accent-primary)] hover:underline break-all text-right">
                {project.previewLink}
              </a>
            </div>
          )}
          {project.appLink && (
            <div className="flex items-start justify-between gap-3">
              <span className="text-sm text-[var(--text-secondary)]">App Link</span>
              <a href={project.appLink} target="_blank" rel="noopener noreferrer"
                className="text-sm font-medium text-[var(--accent-primary)] hover:underline break-all text-right">
                {project.appLink}
              </a>
            </div>
          )}
          {project.zipFilePath && (
            <div className="flex items-start justify-between gap-3">
              <span className="text-sm text-[var(--text-secondary)]">ZIP</span>
              <span className="text-sm font-medium text-[var(--text-primary)] break-all text-right">{project.zipFilePath}</span>
            </div>
          )}
          {project.developmentPrice !== undefined && project.developmentPrice !== null && (
            <div className="flex items-start justify-between gap-3">
              <span className="text-sm text-[var(--text-secondary)]">Development Price</span>
              <span className="text-sm font-medium text-[var(--text-primary)]">{fmtMoney(project.developmentPrice, 2)}</span>
            </div>
          )}
          {project.totalPrice !== undefined && project.totalPrice !== null && (
            <div className="flex items-start justify-between gap-3">
              <span className="text-sm text-[var(--text-secondary)]">Total Price</span>
              <span className="text-sm font-medium text-[var(--text-primary)]">{fmtMoney(project.totalPrice, 2)}</span>
            </div>
          )}
        </div>
      )}

      {/* Mandatory Use Cases */}
      <div className="mb-6 bg-[var(--surface-base)] rounded-xl border border-[var(--border-subtle)] shadow-sm p-4 sm:p-5 lg:p-6">
        <h2 className="text-lg sm:text-xl font-semibold text-[var(--text-primary)] mb-4">Mandatory Use Cases</h2>
        {mandatoryUseCases.length === 0 ? (
          <p className="text-sm text-[var(--text-muted)]">No mandatory use cases for this project.</p>
        ) : (
          <div className="space-y-3">
            {mandatoryUseCases.map((u) => (
              <div key={u.projectUseCaseId}
                className="flex items-center gap-3 p-3 rounded-lg bg-[var(--surface-raised)] border border-[var(--border-subtle)]">
                <div className="flex-1 min-w-0">
                  <div className="text-base sm:text-sm font-medium text-[var(--text-primary)]">{u.name}</div>
                  {u.description && <div className="text-sm text-[var(--text-secondary)] mt-0.5">{u.description}</div>}
                </div>
                <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-red-600/15 text-red-700 dark:text-red-500 border border-red-600/25">Mandatory</span>
                <div className="text-base sm:text-sm font-semibold text-[var(--text-primary)] text-right">{fmtMoney(u.price, 2)}</div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Open Incidents - UC-025 */}
      {incidents.filter((i) => i.status === 'Open').length > 0 && (
        <div className="mb-6 bg-[var(--surface-base)] rounded-xl border border-[var(--border-subtle)] shadow-sm p-4 sm:p-5 lg:p-6">
          <h2 className="text-lg sm:text-xl font-semibold text-[var(--text-primary)] mb-4">
            {t('adminProjectDetail.openIncidents', 'Open Incidents')}
          </h2>
          <div className="space-y-3">
            {incidents.filter((i) => i.status === 'Open').map((incident) => (
              <div key={incident.incidentId}
                className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 p-3 rounded-lg border border-[var(--border-subtle)] bg-[var(--surface-raised)]">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-base sm:text-sm font-medium text-[var(--text-primary)]">
                      {t('adminProjectDetail.incidentNum', 'Incident')} #{incident.incidentId}
                    </span>
                    <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-red-600/15 text-red-700 border border-red-600/25">
                      {incident.status}
                    </span>
                  </div>
                  <div className="text-sm text-[var(--text-secondary)] mb-1 whitespace-pre-wrap break-words">
                    {incident.description}
                  </div>
                  <div className="text-xs text-[var(--text-muted)]">
                    {t('adminProjectDetail.reported', 'Reported')}: {new Date(incident.creationDate).toLocaleDateString()}
                  </div>
                </div>
                <div className="flex gap-2 sm:w-auto w-full">
                  <button
                    onClick={() => navigate(`/admin/incidents/${incident.incidentId}`)}
                    className="flex-1 sm:flex-none px-3 py-1.5 bg-[var(--accent-primary)] text-[var(--accent-text)] rounded-lg hover:bg-[var(--accent-secondary)] text-sm font-medium transition-colors">
                    {t('adminProjectDetail.categorize', 'Categorize')}
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Change Requests — partitioned into Captured/pending (actionable)
          and Processed (read-only archive). Priority is not surfaced —
          admins sort by submission date and the data isn't user-visible
          anywhere else, so the badge was noise. */}
      {(() => {
        const pendingCRs = changeRequests.filter(isPendingCR);
        const resolvedCRs = changeRequests.filter((cr) => !isPendingCR(cr));
        const allPendingSelected =
          pendingCRs.length > 0 && pendingCRs.every((cr) => selectedCRIds.has(cr.changeRequestId));
        const someSelected = selectedCRIds.size > 0;

        const statusBadgeClass = (s: string) =>
          // Both Captured and New render as the pending colour — same meaning
          // from the admin's POV.
          s === 'Captured' || s === 'New' ? 'bg-blue-600/15 text-blue-700 border-blue-600/25' :
          s === 'Processed' ? 'bg-green-600/15 text-green-700 border-green-600/25' :
          s === 'Finished' ? 'bg-green-600/15 text-green-700 border-green-600/25' :
          'bg-gray-600/15 text-gray-700 border-gray-600/25';

        const renderRow = (cr: ChangeRequest, opts: { selectable: boolean; actionable: boolean }) => {
          const screen = screens.find((s) => s.screenId === cr.screenId);
          const isSelected = selectedCRIds.has(cr.changeRequestId);
          return (
            <div key={cr.changeRequestId}
              className={`flex flex-col sm:flex-row sm:items-start gap-3 p-3 rounded-lg border transition-colors ${
                isSelected
                  ? 'border-[var(--accent-primary)] bg-[var(--accent-primary)]/5'
                  : 'border-[var(--border-subtle)] bg-[var(--surface-raised)]'
              }`}>
              {opts.selectable && (
                <div className="pt-0.5 flex-shrink-0">
                  <input
                    type="checkbox"
                    checked={isSelected}
                    onChange={() => toggleCRSelected(cr.changeRequestId)}
                    disabled={bulkBusy}
                    aria-label={t('adminProjectDetail.selectCR', 'Select change request')}
                    className="w-4 h-4 rounded border-[var(--border-default)] accent-[var(--accent-primary)]"
                  />
                </div>
              )}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-2 flex-wrap">
                  {cr.title && (
                    <span className="text-base sm:text-sm font-medium text-[var(--text-primary)]">{cr.title}</span>
                  )}
                  <span className={`px-2 py-0.5 text-xs font-medium rounded-full border ${statusBadgeClass(cr.status)}`}>
                    {cr.status}
                  </span>
                </div>
                <div className="text-sm text-[var(--text-secondary)] mb-1 whitespace-pre-wrap break-words">{cr.description}</div>
                {screen && (
                  <div className="text-xs text-[var(--text-muted)]">Screen: {screen.name}</div>
                )}
                <div className="text-xs text-[var(--text-muted)]">
                  Created: {new Date(cr.creationDate).toLocaleDateString()}
                </div>
              </div>
              {opts.actionable && (
                <div className="flex gap-2 sm:flex-col sm:w-auto w-full flex-shrink-0">
                  <button
                    type="button"
                    onClick={() => handleRowProcessed(cr.changeRequestId)}
                    disabled={bulkBusy}
                    className="flex-1 sm:flex-none px-3 py-1.5 bg-green-600 text-white rounded-lg hover:bg-green-700 text-sm font-medium transition-colors disabled:opacity-50"
                  >
                    {t('adminProjectDetail.markProcessed', 'Mark as Processed')}
                  </button>
                </div>
              )}
              {cr.status === 'Finished' && cr.newPreviewLink && (
                <div className="sm:w-auto w-full flex-shrink-0">
                  <a
                    href={cr.newPreviewLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center px-3 py-1.5 bg-green-600 text-white rounded-lg hover:bg-green-700 text-sm font-medium transition-colors">
                    View Updated
                  </a>
                </div>
              )}
            </div>
          );
        };

        return (
          <>
            {/* Pending (Captured) change requests — actionable by the admin */}
            {pendingCRs.length > 0 && (
              <div className="mb-6 bg-[var(--surface-base)] rounded-xl border border-[var(--border-subtle)] shadow-sm p-4 sm:p-5 lg:p-6">
                {/* Fixed-height header row; swaps content based on selection
                    so the list below doesn't shift. */}
                <div className="flex items-center gap-3 mb-4 min-h-[2.25rem] flex-wrap">
                  {someSelected ? (
                    <>
                      <span className="text-base sm:text-lg font-semibold text-[var(--text-primary)]">
                        {t('adminProjectDetail.selectedCount', '{{count}} selected').replace('{{count}}', String(selectedCRIds.size))}
                      </span>
                      <div className="ml-auto flex items-center gap-2">
                        <button
                          type="button"
                          onClick={handleBulkProcessed}
                          disabled={bulkBusy}
                          className="px-3 py-1.5 bg-green-600 text-white rounded-lg hover:bg-green-700 text-sm font-medium transition-colors disabled:opacity-50"
                        >
                          {t('adminProjectDetail.markProcessed', 'Mark as Processed')}
                        </button>
                        <button
                          type="button"
                          onClick={() => setSelectedCRIds(new Set())}
                          disabled={bulkBusy}
                          className="px-3 py-1.5 text-[var(--text-secondary)] hover:text-[var(--text-primary)] text-sm font-medium transition-colors"
                        >
                          {t('adminProjectDetail.clearSelection', 'Clear')}
                        </button>
                      </div>
                    </>
                  ) : (
                    <>
                      <h2 className="text-lg sm:text-xl font-semibold text-[var(--text-primary)]">
                        {t('adminProjectDetail.changeRequestsHeading', 'Change Requests')}
                      </h2>
                      {pendingCRs.length > 1 && (
                        <label className="ml-auto inline-flex items-center gap-2 text-sm text-[var(--text-secondary)] cursor-pointer">
                          <input
                            type="checkbox"
                            checked={allPendingSelected}
                            onChange={toggleSelectAllPending}
                            className="w-4 h-4 rounded border-[var(--border-default)] accent-[var(--accent-primary)]"
                          />
                          {t('adminProjectDetail.selectAll', 'Select all')}
                        </label>
                      )}
                    </>
                  )}
                </div>

                <div className="space-y-3">
                  {pendingCRs.map((cr) => renderRow(cr, { selectable: true, actionable: true }))}
                </div>
              </div>
            )}

            {/* Processed Change Requests — archive view, no actions. */}
            {resolvedCRs.length > 0 && (
              <div className="mb-6 bg-[var(--surface-base)] rounded-xl border border-[var(--border-subtle)] shadow-sm p-4 sm:p-5 lg:p-6">
                <h2 className="text-lg sm:text-xl font-semibold text-[var(--text-primary)] mb-4">
                  {t('adminProjectDetail.processedChangeRequestsHeading', 'Processed Change Requests')}
                </h2>
                <div className="space-y-3">
                  {resolvedCRs.map((cr) => renderRow(cr, { selectable: false, actionable: false }))}
                </div>
              </div>
            )}
          </>
        );
      })()}

      {/* Optional Use Cases */}
      {optionalUseCases.length > 0 && (
        <div className="mb-6 bg-[var(--surface-base)] rounded-xl border border-[var(--border-subtle)] shadow-sm p-4 sm:p-5 lg:p-6">
          <h2 className="text-lg sm:text-xl font-semibold text-[var(--text-primary)] mb-4">Optional Use Cases</h2>
          <div className="space-y-3">
            {optionalUseCases.map((u) => (
              <div key={u.projectUseCaseId}
                className="flex items-center gap-3 p-3 rounded-lg border border-[var(--border-subtle)]">
                <div className="flex-1 min-w-0">
                  <div className="text-base sm:text-sm font-medium text-[var(--text-primary)]">{u.name}</div>
                  {u.description && <div className="text-sm text-[var(--text-secondary)] mt-0.5">{u.description}</div>}
                  {u.implementationStatus === 'Implemented' && (
                    <div className="text-xs text-green-600 mt-0.5">Already implemented</div>
                  )}
                </div>
                <div className="text-base sm:text-sm font-semibold text-[var(--text-primary)] text-right">{fmtMoney(u.price, 2)}</div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default AdminProjectDetailPage;
